import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { fetchAllProducts, type ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { FilterBar } from "@/components/FilterBar";
import { LocaleLink } from "@/components/LocaleLink";
import { useT } from "@/lib/i18n";
import {
  SIZE_BUCKETS,
  SIZE_SEARCH_QUERY,
  bucketize,
  getSizeBucketBySlug,
} from "@/lib/sizes";
import { SITE_URL, breadcrumbJsonLd } from "@/lib/seo";

export const Route = createFileRoute("/en/storrelse/$range")({
  loader: async ({ params }) => {
    const bucket = getSizeBucketBySlug(params.range);
    if (!bucket) throw notFound();
    // Wide net then filter — Storefront API doesn't allow numeric range queries
    // on description, so we over-fetch and apply parseDiameterCm() in memory.
    const all = await fetchAllProducts({
      query: SIZE_SEARCH_QUERY,
      sortKey: "BEST_SELLING",
      reverse: false,
      max: 1000,
    });

    const grouped = bucketize(all);
    const products = grouped.get(bucket.slug) ?? [];
    const counts: Record<string, number> = {};
    for (const [slug, list] of grouped) counts[slug] = list.length;
    return { bucket, products, counts };
  },
  head: ({ loaderData, params }) => {
    const b = loaderData?.bucket;
    const url = `${SITE_URL}/en/storrelse/${params?.range ?? ""}`;
    const title = b
      ? `Vintage lamps ${b.labelEn} — Farveladeland`
      : "Size — Farveladeland";
    const desc = b?.introEn ?? "Vintage lamps sorted by diameter.";
    const crumb = b
      ? breadcrumbJsonLd([
          { name: "Home", url: `${SITE_URL}/en` },
          { name: "Size", url: `${SITE_URL}/en/storrelse` },
          { name: b.labelEn, url },
        ])
      : null;
    return {
      meta: [
        { title },
        { name: "description", content: desc.slice(0, 160) },
        { property: "og:title", content: title },
        { property: "og:description", content: desc.slice(0, 160) },
      ],
      scripts: crumb
        ? [{ type: "application/ld+json", children: JSON.stringify(crumb) }]
        : [],
    };
  },
  component: SizePage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
        <p className="eyebrow">Ups</p>
        <h1 className="mt-4 font-display text-5xl">Noget gik galt</h1>
        <p className="mt-4 text-muted-foreground">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
        >
          Prøv igen
        </button>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
      <p className="eyebrow">404</p>
      <h1 className="mt-4 font-display text-5xl">Den størrelse har vi ikke</h1>
      <LocaleLink
        to="/storrelse"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
      >
        Se alle størrelser →
      </LocaleLink>
    </div>
  ),
});

function SizePage() {
  const { bucket, products, counts } = Route.useLoaderData();
  const { t, locale } = useT();
  const intro = locale === "da" ? bucket.introDa : bucket.introEn;
  const label = locale === "da" ? bucket.labelDa : bucket.labelEn;
  const others = SIZE_BUCKETS.filter(
    (b) => b.slug !== bucket.slug && (counts[b.slug] ?? 0) > 0
  );
  const [filtered, setFiltered] = useState<ShopifyProduct[]>(products);
  const onFilterChange = useCallback(
    (next: ShopifyProduct[]) => setFiltered(next),
    [],
  );

  // Silhouette icon sized by the bucket's average diameter (matches the
  // home-page mushroom silhouette used elsewhere in the brand).
  const mid = bucket.max === Infinity ? 50 : (bucket.min + bucket.max) / 2;
  const iconSize = Math.min(176, 56 + mid * 2.2);

  return (
    <>
      <section className={`relative pt-40 pb-16 md:pt-48 md:pb-24 ${bucket.panel}`}>
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <nav className="mb-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-foreground/55">

            <LocaleLink to="/" className="hover:text-foreground">
              {t("size.crumbHome")}
            </LocaleLink>
            <span>/</span>
            <LocaleLink to="/storrelse" className="hover:text-foreground">
              {t("size.crumbSizes")}
            </LocaleLink>
            <span>/</span>
            <span className="text-foreground">{label}</span>
          </nav>

          <div className="grid items-end gap-10 md:grid-cols-[auto_1fr]">
            <div className="flex h-32 w-32 items-center justify-center md:h-44 md:w-44">
              <svg
                width={iconSize}
                height={iconSize}
                viewBox="0 0 40 48"
                fill="currentColor"
                className="text-foreground/80"
                aria-hidden
              >
                <path d="M9 20 C9 9, 14 4, 20 4 C26 4, 31 9, 31 20 C31 26, 27 28, 20 28 C13 28, 9 26, 9 20 Z" />
                <rect x="17" y="28" width="6" height="10" />
                <path d="M10 38 L30 38 L27 43 L13 43 Z" />
              </svg>
            </div>

            <div>
              <p className="eyebrow">{bucket.italian}</p>
              <h1 className="mt-3 font-display text-[clamp(3rem,8vw,7rem)] leading-[0.92]">
                {label}
              </h1>
              <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-foreground/60">
                {t("size.range")} ·{" "}
                {bucket.max === Infinity
                  ? `${bucket.min}+ cm`
                  : `${bucket.min}–${bucket.max} cm`}
              </p>
              <p className="mt-6 max-w-xl text-pretty text-base leading-relaxed text-foreground/75">
                {intro}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pt-16 pb-24 md:px-10 md:pt-20 md:pb-32">
        {products.length === 0 ? (
          <div className="flex flex-col items-center gap-6 py-20 text-center">
            <p className="max-w-md text-muted-foreground">{t("size.empty")}</p>
            <LocaleLink
              to="/collections/$handle"
              params={{ handle: "murano-lamper" }}
              className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background hover:bg-terracotta"
            >
              {t("size.seeAll")} →
            </LocaleLink>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-end justify-between">
              <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
                {t("size.count", { n: products.length })}
              </p>
              <LocaleLink
                to="/storrelse"
                className="text-[11px] font-medium uppercase tracking-[0.24em] text-foreground/70 hover:text-terracotta"
              >
                {t("size.allSizes")} →
              </LocaleLink>
            </div>
            <FilterBar products={products} onChange={onFilterChange} />
            {filtered.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                {t("filter.noMatch")}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-12 sm:grid-cols-3 lg:grid-cols-4">
                {filtered.map((p: ShopifyProduct) => (
                  <ProductCard key={p.node.id} product={p} />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* Other sizes — skip entirely when no other bucket has stock */}
      {others.length > 0 && (
        <section className="mx-auto max-w-[1500px] border-t border-border px-6 py-16 md:px-10 md:py-24">
          <p className="eyebrow">{t("size.allSizes")}</p>
          <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-3 md:gap-3 lg:grid-cols-5">
            {others.map((b) => (
              <LocaleLink
                key={b.slug}
                to="/storrelse/$range"
                params={{ range: b.slug }}
                className={`group flex aspect-[4/3] flex-col justify-between rounded-sm p-3 transition-transform duration-500 hover:-translate-y-1 ${b.panel}`}
              >
                <span className="text-[9px] uppercase tracking-[0.24em] text-foreground/50">
                  {b.italian}
                </span>
                <p className="font-display text-base text-foreground">
                  {locale === "da" ? b.labelDa : b.labelEn}
                </p>
              </LocaleLink>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
